import React, { useCallback, useMemo, useState } from "react";
import { Button, Checkbox, Flex, Text } from "@jsq/ds/core";
import { useGetAllInvestmentEntitiesQuery } from "../../../../graphql/supergraphHooks";
import { toDbId } from "../../../../utils/graphql_utils";
import { COMPLIANCE_PROGRAMS } from "../../constants";
import { FilterPopover } from "./FilterPopover";
import { checkboxItem, checkboxList, dateInput, filterPanelFooter } from "./styles_vanilla.css";

type Props = {
  value: number[] | null;
  onChange: (entityIds: number[] | null) => void;
};

type EntityOption = {
  id: number;
  name: string;
};

const buildTriggerLabel = (selected: number[] | null, entities: EntityOption[]): string => {
  if (!selected?.length) return "Entity";
  if (selected.length === 1) {
    const match = entities.find((e) => e.id === selected[0]);
    return match?.name ?? "Entity";
  }
  return `${selected.length} entities`;
};

export const EntityFilter: React.FC<Props> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [localSelected, setLocalSelected] = useState<number[]>([]);

  const { data } = useGetAllInvestmentEntitiesQuery({
    variables: { compliancePrograms: COMPLIANCE_PROGRAMS },
  });
  const entities: EntityOption[] = useMemo(
    () =>
      data?.investmentEntities?.edges.map(({ node }) => ({
        id: toDbId(node.id),
        name: node.name,
      })) ?? [],
    [data],
  );

  const filteredEntities = useMemo(() => {
    const q = search.toLowerCase();
    return entities.filter((e) => e.name.toLowerCase().includes(q));
  }, [entities, search]);

  const isAllSelected = entities.length > 0 && localSelected.length === entities.length;

  const toggleSelectAll = useCallback(() => {
    setLocalSelected(isAllSelected ? [] : entities.map((e) => e.id));
  }, [isAllSelected, entities]);

  const toggleEntity = useCallback((id: number) => {
    setLocalSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const handleApply = useCallback(() => {
    onChange(isAllSelected ? null : localSelected);
    setIsOpen(false);
  }, [isAllSelected, localSelected, onChange]);

  const handleOpen = useCallback(() => {
    setLocalSelected(value ?? entities.map((e) => e.id));
    setSearch("");
    setIsOpen((o) => !o);
  }, [value, entities]);

  return (
    <FilterPopover
      label={buildTriggerLabel(value, entities)}
      isActive={value != null}
      isOpen={isOpen}
      onToggle={handleOpen}
      onClose={() => setIsOpen(false)}
    >
      <input
        type="text"
        placeholder="Search entity"
        className={dateInput}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className={checkboxList}>
        <Checkbox
          label="(Select all)"
          checked={isAllSelected}
          indeterminate={localSelected.length > 0 && !isAllSelected}
          onChange={toggleSelectAll}
          wrapperClassName={checkboxItem}
        />
        {filteredEntities.map((entity) => (
          <Checkbox
            key={entity.id}
            label={entity.name}
            checked={localSelected.includes(entity.id)}
            onChange={() => toggleEntity(entity.id)}
            wrapperClassName={checkboxItem}
          />
        ))}
        {filteredEntities.length === 0 && (
          <Text variant="regular-sm">No matching entities</Text>
        )}
      </div>

      <Flex justifyContent="flex-end" alignItems="center" className={filterPanelFooter}>
        <Button variant="primary" onClick={handleApply}>
          Apply
        </Button>
      </Flex>
    </FilterPopover>
  );
};
